import { CheckCircle, Circle, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  Collapse,
  CircularProgress,
} from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { EntityType, entityType, TasksQueryDto } from '@nohara-dxdev/mt-common';
import { permission } from '@nohara-dxdev/oidc-core';
import { formatDateTime, PaginatedDataGrid } from '@nohara-dxdev/react-core';
import { OidcProtectedComponent, useScopeIds } from '@nohara-dxdev/react-oidc';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Cell } from '@/components';
import { EditItemButton } from '@/features/items';
import { DashboardTaskGroupEntity, TaskEntity } from '@/types/api';

import { useDashboardTaskGroups, useTasks, useUpdateTask } from '../api';

import { CreateTaskButton } from './create-task-button';
import { GlobalCreateTaskButton } from './global-create-task-button';
import { TaskStatusChip } from './task-status-chip';
import { ViewTaskButton } from './view-task-button';

const readTaskPermissions = [permission.task.read];
const updateTaskPermissions = [permission.task.update];

export interface TasksListProps {
  filters?: TasksQueryDto;
  showEditButton?: boolean;
  // 'dashboard' groups tasks (groupSize > 1 = expandable header); every other
  // list must keep the default 'flat' mode.
  displayMode?: 'dashboard' | 'flat';
}

const getEntityTypeColor = (type: string) => {
  switch (type) {
    case 'item':
      return 'primary';
    case 'room':
      return 'secondary';
    case 'building':
      return 'success';
    case 'floor':
      return 'info';
    case 'task':
      return 'warning';
    default:
      return 'default';
  }
};

const TaskActionsCell = ({ row }: { row: TaskEntity }) => {
  const { t } = useTranslation();
  const updateTask = useUpdateTask(row.id);
  const [loading, setLoading] = useState(false);
  const scopeIds = useScopeIds();

  const subject = useMemo(
    () => ({
      ...scopeIds,
      taskId: row.id,
    }),
    [scopeIds, row.id],
  );

  const handleToggleStatus = async () => {
    setLoading(true);
    try {
      await updateTask.mutateAsync({ status: row.status === 'open' ? 'closed' : 'open' });
    } finally {
      setLoading(false);
    }
  };

  const StatusIcon = row.status === 'open' ? Circle : CheckCircle;

  return (
    <OidcProtectedComponent requireAll={updateTaskPermissions} subject={subject}>
      <Cell centered>
        <Tooltip title={t(`tasks.status.toggleTo.${row.status === 'open' ? 'closed' : 'open'}`)}>
          <span>
            <IconButton size="small" onClick={() => void handleToggleStatus()} disabled={loading}>
              <StatusIcon color={row.status === 'open' ? 'inherit' : 'success'} fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Cell>
    </OidcProtectedComponent>
  );
};

const getTaskRowId = (row: TaskEntity) => row.id;

// --- Flat mode (Room/Item/location-filtered, and the default) ---

const TasksDataGrid = ({
  filters,
  showEditButton,
}: {
  filters: TasksQueryDto;
  showEditButton: boolean;
}) => {
  const { t } = useTranslation();
  const scopeIds = useScopeIds();
  const tasksQuery = useTasks(filters);

  const context = useMemo(
    () => ({
      ...scopeIds,
      ...filters,
    }),
    [scopeIds, filters],
  );

  const columns = useMemo(
    (): GridColDef<TaskEntity>[] => [
      {
        field: 'createdAt',
        headerName: t('tasks.list.columns.createdAt'),
        flex: 1.2,
        valueFormatter: (value: number) => formatDateTime(value),
      },
      {
        field: 'author',
        headerName: t('tasks.list.columns.author'),
        flex: 1,
        renderCell: (params: GridRenderCellParams<TaskEntity>) => (
          <Cell>
            <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
              {(params.value?.name ?? params.value?.id ?? '').charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="body2" noWrap>
              {params.value?.name}
            </Typography>
          </Cell>
        ),
      },
      {
        field: 'title',
        headerName: t('tasks.list.columns.title'),
        flex: 1.5,
        renderCell: (params: GridRenderCellParams<TaskEntity>) => (
          <Cell>
            <Tooltip title={params.value}>
              <Typography variant="body2" noWrap>
                {params.value}
              </Typography>
            </Tooltip>
          </Cell>
        ),
      },
      // Removed description column, detail is available in view modal
      {
        field: 'status',
        headerName: t('tasks.list.columns.status'),
        flex: 0.8,
        renderCell: (params) => <TaskStatusChip status={params.value} />,
      },
      {
        field: 'entityId',
        headerName: t('tasks.list.columns.entityId'),
        flex: 1,
        renderCell: (params) => (
          <Cell>
            {params.row.entityType === entityType.item ? (
              <Chip label={params.row.entity.itemNumber} sx={{ cursor: 'default' }} size="small" />
            ) : (
              <Tooltip title={params.row.entityId}>
                <Chip
                  label={`${params.row.entityId.slice(0, 4)}...${params.row.entityId.slice(-4)}`}
                  size="small"
                  sx={{ fontFamily: 'monospace', cursor: 'default' }}
                />
              </Tooltip>
            )}
          </Cell>
        ),
      },
      {
        field: 'entityType',
        headerName: t('tasks.list.columns.entityType'),
        flex: 0.8,
        renderCell: (params) => (
          <Chip
            label={t(`conversations.entityType.${params.value}`)}
            color={getEntityTypeColor(params.value)}
            size="small"
          />
        ),
      },
      {
        field: 'actions',
        headerName: t('tasks.list.columns.actions'),
        flex: 0.8,
        // Floor for up to 3 row-action icons (edit / view / toggle status) so
        // they stay visible at narrow viewport widths (MT1-1358).
        minWidth: 160,
        align: 'right',
        headerAlign: 'right',
        sortable: false,
        renderCell: (params) => (
          <Cell end>
            {showEditButton && params.row.entityType === entityType.item && (
              <EditItemButton item={params.row.entity} />
            )}
            <ViewTaskButton task={params.row} />
            <TaskActionsCell row={params.row} />
          </Cell>
        ),
      },
    ],
    [t, showEditButton],
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          {t('tasks.list.title', { count: tasksQuery.totalCount })}
        </Typography>
        {(filters.entityId && filters.entityType) || filters.locationId ? (
          <CreateTaskButton
            entityId={filters.locationId ?? filters.entityId ?? ''}
            entityType={(filters.locationId ? 'room' : filters.entityType) as EntityType}
            showName
            variant="contained"
          />
        ) : Object.values(filters).filter(Boolean).length === 0 ? (
          <GlobalCreateTaskButton showName />
        ) : null}
      </Box>

      <OidcProtectedComponent requireAll={readTaskPermissions} subject={context}>
        <PaginatedDataGrid
          paginatedQuery={tasksQuery}
          columns={columns}
          showTopPagination
          getRowId={getTaskRowId}
        />
      </OidcProtectedComponent>
    </Box>
  );
};

// --- Dashboard mode ---

const TaskRowCells = ({ task, showEditButton }: { task: TaskEntity; showEditButton: boolean }) => {
  const { t } = useTranslation();
  return (
    <>
      <TableCell>{formatDateTime(task.createdAt)}</TableCell>
      <TableCell>
        <Cell>
          <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
            {(task.author?.name ?? task.author?.id ?? '').charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="body2" noWrap>
            {task.author?.name ?? task.authorId}
          </Typography>
        </Cell>
      </TableCell>
      <TableCell>
        <Tooltip title={task.title}>
          <Typography variant="body2" noWrap>
            {task.title}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>
        <TaskStatusChip status={task.status} />
      </TableCell>
      <TableCell>
        {task.entityType === entityType.item ? (
          <Chip label={task.entity.itemNumber} sx={{ cursor: 'default' }} size="small" />
        ) : (
          <Tooltip title={task.entityId}>
            <Chip
              label={`${task.entityId.slice(0, 4)}...${task.entityId.slice(-4)}`}
              size="small"
              sx={{ fontFamily: 'monospace', cursor: 'default' }}
            />
          </Tooltip>
        )}
      </TableCell>
      <TableCell>
        <Chip
          label={t(`conversations.entityType.${task.entityType}`)}
          color={getEntityTypeColor(task.entityType)}
          size="small"
        />
      </TableCell>
      <TableCell align="right">
        <Cell end>
          {showEditButton && task.entityType === entityType.item && (
            <EditItemButton item={task.entity} />
          )}
          <ViewTaskButton task={task} />
          <TaskActionsCell row={task} />
        </Cell>
      </TableCell>
    </>
  );
};

const TaskGroupRow = ({
  group,
  showEditButton,
}: {
  group: DashboardTaskGroupEntity;
  showEditButton: boolean;
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (group.groupSize === 1) {
    const task = group.tasks[0];
    return (
      <TableRow hover>
        <TableCell />
        <TaskRowCells task={task} showEditButton={showEditButton} />
      </TableRow>
    );
  }

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 48 }}>
          <IconButton
            size="small"
            aria-label={t(expanded ? 'common.collapse' : 'common.expand')}
            aria-expanded={expanded}
            onClick={() => {
              setExpanded((prev) => !prev);
            }}
          >
            {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{formatDateTime(group.newestChildCreatedAt)}</TableCell>
        <TableCell>
          <Cell>
            <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
              {(group.author?.name ?? group.author?.id ?? '').charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="body2" noWrap>
              {group.author?.name ?? group.authorId}
            </Typography>
          </Cell>
        </TableCell>
        <TableCell>
          <Cell>
            <Tooltip title={group.groupTitle}>
              <Typography variant="body2" fontWeight="bold" noWrap>
                {group.groupTitle}
              </Typography>
            </Tooltip>
            <Chip
              label={group.groupSize}
              size="small"
              color="primary"
              sx={{ ml: 1, height: 20, minWidth: 20, '& .MuiChip-label': { px: 0.75 } }}
            />
          </Cell>
        </TableCell>
        <TableCell>
          <TaskStatusChip status={group.status} />
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            {t('tasks.list.columns.entitiesCount', { count: group.groupSize })}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={t(`conversations.entityType.${group.entityType}`)}
            color={getEntityTypeColor(group.entityType)}
            size="small"
          />
        </TableCell>
        {/* Actions only ever live on child rows, once expanded. */}
        <TableCell align="right" />
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Table size="small">
              <TableBody>
                {group.tasks.map((task) => (
                  <TableRow key={task.id} hover>
                    <TableCell sx={{ width: 48 }} />
                    <TaskRowCells task={task} showEditButton={showEditButton} />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const DashboardTaskGroupsList = ({ showEditButton }: { showEditButton: boolean }) => {
  const { t } = useTranslation();
  const scopeIds = useScopeIds();
  const groupsQuery = useDashboardTaskGroups();

  // Real task count, not rendered row count -- a group header is just UI.
  const totalTaskCount = useMemo(
    () => groupsQuery.allData.reduce((sum, group) => sum + group.groupSize, 0),
    [groupsQuery.allData],
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{t('tasks.list.title', { count: totalTaskCount })}</Typography>
        <GlobalCreateTaskButton showName />
      </Box>

      <OidcProtectedComponent requireAll={readTaskPermissions} subject={scopeIds}>
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48 }} />
                <TableCell>{t('tasks.list.columns.createdAt')}</TableCell>
                <TableCell>{t('tasks.list.columns.author')}</TableCell>
                <TableCell>{t('tasks.list.columns.title')}</TableCell>
                <TableCell>{t('tasks.list.columns.status')}</TableCell>
                <TableCell>{t('tasks.list.columns.entityId')}</TableCell>
                <TableCell>{t('tasks.list.columns.entityType')}</TableCell>
                <TableCell align="right">{t('tasks.list.columns.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupsQuery.isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              ) : groupsQuery.data?.length ? (
                groupsQuery.data.map((group) => (
                  <TaskGroupRow key={group.groupId} group={group} showEditButton={showEditButton} />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="body2" color="text.secondary">
                      {t('tasks.list.empty')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={groupsQuery.totalCount}
            page={groupsQuery.pageIndex}
            rowsPerPage={groupsQuery.pageSize}
            onPageChange={(_event, newPage) => {
              groupsQuery.setPageIndex(newPage);
            }}
            onRowsPerPageChange={(event) => {
              groupsQuery.setPageSize(Number(event.target.value));
            }}
          />
        </TableContainer>
      </OidcProtectedComponent>
    </Box>
  );
};

export const TasksList = ({
  filters = {},
  showEditButton = false,
  displayMode = 'flat',
}: TasksListProps) => {
  if (displayMode === 'dashboard') {
    return <DashboardTaskGroupsList showEditButton={showEditButton} />;
  }

  return <TasksDataGrid filters={filters} showEditButton={showEditButton} />;
};
